import { Dispatch, FunctionComponent, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";

// Hooks
import { useMutation, useMutationState } from "@tanstack/react-query";
import { useNotificationMutations } from "@/Common/hooks/Notifications";
// Components
import DateDiff from "@Layouts/components/DateDiff";
import { NotificationSpan } from "@/Layouts/views/Header/components/Notifications";
// Types
import { INotification } from "@/Common/api/types/notifications";
// Helpers
import { countRows } from "@/Common/helpers/sizing";
// MUI
import { Box, Divider, Stack, Step, StepIconProps, StepLabel, IconButton, Tooltip } from "@mui/material";
// Icons
import InsightsIcon from "@mui/icons-material/Insights";
import ReviewsIcon from "@mui/icons-material/Reviews";
import SettingsIcon from "@mui/icons-material/Settings";
import MailIcon from "@mui/icons-material/Mail";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SchoolIcon from "@mui/icons-material/School";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import { ReactComponent as AchievementsIcon } from "@/assets/icons/dashboard/AchievementsIcon.svg";
import CustomArchivedIcon from "./CustomArchivedIcon";
import CustomDeleteIcon from "./CustomDeleteIcon";

const ICONS = {
	Settings: <SettingsIcon />,
	Incentives: <EmojiEventsIcon />,
	Commissions: <NextWeekIcon className="mb-[3px]" />,
	Surveys: <InsightsIcon />,
	Training: <SchoolIcon />,
	Achievements: <AchievementsIcon />,
	Ratings: <ReviewsIcon />,
	Default: <MailIcon />,
};

const StepIcon: FunctionComponent<StepIconProps> = ({ icon }) => {
	return (
		<Stack
			alignItems="center"
			justifyContent="center"
			className="bg-[#e0e7f1] w-11 h-11 border-[2px] border-neutral-gray text-neutral-gray rounded-full z-10"
		>
			{ICONS[icon as keyof typeof ICONS] ?? ICONS.Default}
		</Stack>
	);
};

function ReadIndicator(props: Readonly<{ isRead: boolean; handleToggle: () => void }>) {
	return (
		<Box
			display="flex"
			flexDirection="row-reverse"
			className="cursor-pointer"
			onClick={(e) => {
				e.stopPropagation();
				props?.handleToggle?.();
			}}
		>
			<Box
				className={clsx("size-[12px] my-3 mr-[5px] rounded-full", {
					"bg-disabled": props?.isRead,
					"bg-primary": !props?.isRead,
				})}
			/>
		</Box>
	);
}

function NotificationActions(
	props: Readonly<{ notification: INotification; isHovered: boolean; archiveAction: () => void; deleteAction: () => void }>
) {
	const { notification, archiveAction, deleteAction, isHovered } = props;
	if (!isHovered) {
		return <Box className="h-[22px] w-[48px]"></Box>;
	}
	return (
		<Box display="flex" gap={0.5}>
			{!notification.archived && (
				<Tooltip title="Archive" arrow placement="left">
					<IconButton className="size-[22px]" onClick={archiveAction}>
						<CustomArchivedIcon className="p-[5px]" />
					</IconButton>
				</Tooltip>
			)}
			<Tooltip title="Delete" arrow placement="right">
				<IconButton className="size-[22px]" onClick={deleteAction} disableRipple>
					<CustomDeleteIcon className="p-[5px]" />
				</IconButton>
			</Tooltip>
		</Box>
	);
}

function useHover() {
	const containerRef = useRef<HTMLDivElement>(null);
	const [hovered, setHovered] = useState(false);

	const onMouseEnter = () => {
		setHovered(true);
	};

	const onMouseLeave = () => {
		setHovered(false);
	};

	useEffect(() => {
		containerRef.current?.addEventListener("mouseenter", onMouseEnter);
		containerRef.current?.addEventListener("mouseleave", onMouseLeave);

		return () => {
			containerRef.current?.removeEventListener("mouseenter", onMouseEnter);
			containerRef.current?.removeEventListener("mouseleave", onMouseLeave);
		};
	}, [containerRef]);

	return { containerRef, hovered };
}

interface INotificationItemProps {
	notification: INotification;
	lastItem: boolean;
	selected: string;
	setSelected: Dispatch<SetStateAction<string>>;
	optimisticArchiveHandlers: {
		onDone?: (notification: INotification) => void;
		onFail?: (notification: INotification) => void;
	};
}
export function NotificationItem(props: Readonly<INotificationItemProps>) {
	const { notification, lastItem, selected, setSelected } = props;
	const { updateReadNotification, updateArchivedNotification } = useNotificationMutations();
	const { hovered, containerRef } = useHover();

	const notificationRef = useRef<HTMLSpanElement>(null);

	const numberOfLines = countRows(notificationRef);

	const [optimisticRead, setOptimisticRead] = useState(notification.read);

	const isMutatingANotification = useMutationState<boolean>({
		filters: { mutationKey: ["toggleNotificationRead"], status: "pending", exact: true },
		select: ({ state }) => state.status === "pending",
	});

	useEffect(() => {
		setOptimisticRead(notification.read);
	}, [setOptimisticRead, notification]);

	const { mutate: toggleNotificationRead, isPending } = useMutation({
		mutationKey: ["toggleNotificationRead"],
		mutationFn: () => updateReadNotification(notification._id, !notification.read, notification.archived),
		onMutate() {
			setOptimisticRead(!notification.read);
		},
		onSuccess() {
			setOptimisticRead(!notification.read);
		},
		onError() {
			setOptimisticRead(notification.read);
		},
	});

	const handleToggleNotificationRead = useCallback(() => {
		if (!isMutatingANotification.some(Boolean) && !isPending) {
			toggleNotificationRead();
		}
	}, [!isMutatingANotification.some(Boolean) && !isPending, toggleNotificationRead]);

	const handleSelect = useCallback(
		(newId: string) => {
			if (selected !== "" && selected === newId) {
				setSelected("");
			} else {
				setSelected(newId);
			}
		},
		[selected, setSelected]
	);

	const { mutate: handleArchiveAction } = useMutation({
		mutationKey: ["handleArchiveAction"],
		mutationFn: () => {
			return updateArchivedNotification(notification._id, { archived: true, read: notification.read });
		},
		onMutate() {
			props?.optimisticArchiveHandlers?.onDone?.(notification);
		},
		onSuccess() {
			props?.optimisticArchiveHandlers?.onDone?.(notification);
		},
		onError() {
			props?.optimisticArchiveHandlers?.onFail?.(notification);
		},
	});

	const handleDeleteAction = () => {
		console.warn("Delete action for notifications is not yet implemented"); // NOSONAR - This is a temporary warning
	};

	return (
		<Step ref={containerRef} className="relative right-0">
			<StepLabel
				StepIconComponent={StepIcon}
				StepIconProps={{
					icon: notification.topic,
				}}
				className="py-[6px] ![align-items:unset]"
			>
				<Stack direction="row" justifyContent="space-between">
					<Box flexGrow={1}>
						<Stack direction="column" className="text-[13px] text-default">
							<Box
								onClick={() => {
									handleSelect(notification._id);
								}}
								className={clsx("font-sans text-default text-[14.5px]/[1.2] transition-all", {
									"line-clamp-2 ellipsis": selected !== notification._id,
									"cursor-pointer": numberOfLines > 2,
								})}
							>
								<NotificationSpan ref={notificationRef} str={notification.message} />
							</Box>
							<DateDiff date={notification.createdAt} noTooltip maxWidth={"100%"} />
						</Stack>
					</Box>
					<Box>
						<ReadIndicator isRead={optimisticRead} handleToggle={handleToggleNotificationRead} />
						<NotificationActions
							notification={notification}
							archiveAction={handleArchiveAction}
							deleteAction={handleDeleteAction}
							isHovered={hovered}
						/>
					</Box>
				</Stack>
				{!lastItem && (
					<>
						<Divider sx={{ borderColor: "#cccccc", marginTop: "10px" }} />
						<Divider className="absolute !h-[calc(100%-50px)] left-[22px] -bottom-[3px]" orientation="vertical" />
					</>
				)}
			</StepLabel>
		</Step>
	);
}
